import { Colors, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { TranslationKey } from '@/i18n-key';

import AI_IMAGE_BG1 from '@/assets/media/bg-1.png';
import AI_IMAGE_BG2 from '@/assets/media/bg-2.png';
import { NeuralLoadingBackground } from '@/components/AiRecommendationPromptModal/components/NeuralLoadingBackground';

interface AiPromptModalImageProps {
    isGenerating?: boolean;
    imageAltKey: TranslationKey;
    overlayGeneratingKey: TranslationKey;
    overlayMessageKey: TranslationKey;
}

export const AiPromptModalImage = ({
    isGenerating = false,
    imageAltKey,
    overlayGeneratingKey,
    overlayMessageKey,
}: AiPromptModalImageProps) => {
    const { t } = useTranslation();

    return (
        <div
            className={`relative hidden flex-1 items-center justify-center overflow-hidden rounded-r-lg p-8 md:flex lg:!block ${
                isGenerating
                    ? 'bg-slate-950'
                    : 'bg-gradient-to-br from-fuchsia-500/20 via-indigo-950 to-slate-900'
            }`}
        >
            {isGenerating ? (
                <NeuralLoadingBackground />
            ) : (
                <>
                    <div
                        className={'pointer-events-none absolute inset-0 mix-blend-lighten'}
                        style={{
                            backgroundImage: `url(${AI_IMAGE_BG1})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center center',
                        }}
                        aria-hidden={true}
                    />
                    <div className={'absolute inset-0 z-10 flex items-center justify-center'}>
                        <img
                            src={AI_IMAGE_BG2}
                            alt={t(imageAltKey)}
                            aria-hidden={true}
                            className={'w-3/4 max-w-full object-contain drop-shadow-2xl'}
                        />
                    </div>
                </>
            )}

            {isGenerating && (
                <div className={'absolute inset-0 z-20 flex items-center justify-center p-4'}>
                    <div
                        className={
                            'w-full max-w-[380px] rounded-2xl bg-white/40 px-5 py-9 text-center text-white shadow-lg backdrop-blur-sm'
                        }
                    >
                        <div className={'mb-4 flex items-center justify-center'}>
                            <Text
                                weight={'semibold'}
                                color={Colors.WHITE}
                                className={'!text-2xl font-semibold text-white'}
                            >
                                {t(overlayGeneratingKey)}
                            </Text>

                            <div
                                className={'ml-3 flex items-center justify-center gap-2'}
                                aria-hidden={true}
                            >
                                <span
                                    className={'animate-dot-1 bg-primary h-2.5 w-2.5 rounded-full'}
                                />
                                <span
                                    className={'animate-dot-2 bg-primary h-2.5 w-2.5 rounded-full'}
                                />
                                <span
                                    className={'animate-dot-3 bg-primary h-2.5 w-2.5 rounded-full'}
                                />
                            </div>
                        </div>
                        <Text
                            size={'small'}
                            color={Colors.WHITE}
                            className={'mb-4 text-center !text-base'}
                        >
                            {t(overlayMessageKey)}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};
